/* eslint-disable function-call-argument-newline */
import { toContext } from 'ol/render';
import { Style, Fill } from 'ol/style';
import { Polygon, MultiPolygon } from 'ol/geom';
import { DEVICE_PIXEL_RATIO } from 'ol/has';

import {
  IMAGE_LOADING,
  IMAGE_LOADED,
  IMAGE_ERROR
} from '../constants';
import { memoizeStyleFunction } from './styleUtils';
import { getCachedImage, getImageLoadingState } from '../imageCache';
import { imageLoadingPolygonStyle, imageErrorPolygonStyle } from './static';
import { getSimpleStroke, getSimpleFill } from './simpleStyles';
import { getGraphicStrokeRenderer } from './graphicStrokeStyle';
import getMarkFillStyle from './graphicFillStyle';

function createPattern(graphic) {
  const { image, width, height } = getCachedImage(
    graphic.externalgraphic.onlineresource
  );
  const cnv = document.createElement('canvas');
  const ctx = cnv.getContext('2d');

  // Calculate image scale factor.
  let imageRatio = DEVICE_PIXEL_RATIO;
  if (graphic.size && height !== graphic.size) {
    imageRatio *= graphic.size / height;
  }

  // Draw image to canvas directly if no scaling necessary.
  if (imageRatio === 1) {
    return ctx.createPattern(image, 'repeat');
  }

  // Scale the image by drawing onto a temp canvas.
  const tempCanvas = document.createElement('canvas');
  const tCtx = tempCanvas.getContext('2d');
  tempCanvas.width = width * imageRatio;
  tempCanvas.height = height * imageRatio;
  // prettier-ignore
  tCtx.drawImage(
    image,
    0, 0, width, height,
    0, 0, width * imageRatio, height * imageRatio
  );

  return ctx.createPattern(tempCanvas, 'repeat');
}

function getExternalGraphicFill(symbolizer) {
  const { graphic } = symbolizer.fill.graphicfill;
  const fillImageUrl = graphic.externalgraphic.onlineresource;

  // Use fallback style when graphicfill image hasn't been loaded yet.
  switch (getImageLoadingState(fillImageUrl)) {
    case IMAGE_LOADED:
      return new Fill({
        color: createPattern(symbolizer.fill.graphicfill.graphic),
      });
    case IMAGE_LOADING:
      return imageLoadingPolygonStyle.getFill();
    case IMAGE_ERROR:
      return imageErrorPolygonStyle.getFill();
    default:
      // Load state of an image should be known at this time, but return 'loading' style as fallback.
      return imageLoadingPolygonStyle.getFill();
  }
}

function polygonStyle(symbolizer) {
  const fillImageUrl =
    symbolizer.fill &&
    symbolizer.fill.graphicfill &&
    symbolizer.fill.graphicfill.graphic &&
    symbolizer.fill.graphicfill.graphic.externalgraphic &&
    symbolizer.fill.graphicfill.graphic.externalgraphic.onlineresource;

  const fillMark =
    symbolizer.fill &&
    symbolizer.fill.graphicfill &&
    symbolizer.fill.graphicfill.graphic &&
    symbolizer.fill.graphicfill.graphic.mark;

  let polygonFill = null;
  if (fillImageUrl) {
    polygonFill = getExternalGraphicFill(symbolizer);
  } else if (fillMark) {
    polygonFill = getMarkFillStyle(symbolizer.fill.graphicfill);
  } else {
    polygonFill = getSimpleFill(symbolizer.fill);
  }

  // When a polygon has a GraphicStroke, use a custom renderer to combine
  // GraphicStroke with fill. This is needed because a custom renderer
  // ignores any stroke, fill and image present in the style.
  if (symbolizer.stroke && symbolizer.stroke.graphicstroke) {
    const renderGraphicStroke = getGraphicStrokeRenderer(symbolizer);
    return new Style({
      renderer: (pixelCoords, renderState) => {
        // First render the fill (if any).
        if (polygonFill) {
          const { feature, context } = renderState;
          const render = toContext(context);
          render.setFillStrokeStyle(polygonFill, undefined);
          const geometryType = feature.getGeometry().getType();
          if (geometryType === 'Polygon') {
            render.drawPolygon(new Polygon(pixelCoords));
          } else if (geometryType === 'MultiPolygon') {
            render.drawMultiPolygon(new MultiPolygon(pixelCoords));
          }
        }

        // Then, render the graphic stroke.
        renderGraphicStroke(pixelCoords, renderState);
      },
    });
  }

  const polygonStroke = getSimpleStroke(symbolizer.stroke);

  return new Style({
    fill: polygonFill,
    stroke: polygonStroke,
  });
}

const cachedPolygonStyle = memoizeStyleFunction(polygonStyle);

/**
 * @private
 * Get an OL line style instance for a feature according to a symbolizer.
 * @param {object} symbolizer SLD symbolizer object.
 * @returns {ol/Style} OpenLayers style instance.
 */
function getPolygonStyle(symbolizer, feature) {
    const olStyle = cachedPolygonStyle(symbolizer);

    if (feature.get('xstate') == null || feature.get('xstate') == 0) {
        olStyle.setZIndex(0);
    } else {
        olStyle.setZIndex(100);
    }

    if (symbolizer && symbolizer.stroke && symbolizer.stroke.styling && symbolizer.stroke.styling.stroke && symbolizer.stroke.styling.stroke.prop) {
        olStyle.getStroke().setColor(feature.get(symbolizer.stroke.styling.stroke.prop));
    }

    if (symbolizer && symbolizer.fill && symbolizer.fill.styling && symbolizer.fill.styling.fill && symbolizer.fill.styling.fill.prop) {
        olStyle.getFill().setColor(feature.get(symbolizer.fill.styling.fill.prop));
    }

    return olStyle;
}

export default getPolygonStyle;
